import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { RecipePreviewMessage, StepInfo } from '../types/protocol';

interface RecipePreviewProps {
  preview: RecipePreviewMessage;
  onConfirm?: () => void;
  onEdit?: (stepId: string) => void;
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

function formatSchedule(value: unknown, t: TFunction): string {
  if (!value || typeof value !== 'object') return '—';
  const obj = value as Record<string, unknown>;
  if (obj['24_7']) return t('recipe.alwaysOpen');
  const weekly = obj.weekly as Array<{ dow: number; start: string; end: string }> | undefined;
  if (!weekly || weekly.length === 0) return '—';
  const sorted = [...weekly].sort((a, b) => a.dow - b.dow);
  const allSame = sorted.every((d) => d.start === sorted[0]!.start && d.end === sorted[0]!.end);
  if (allSame && sorted.length > 1) {
    const days = sorted.map((d) => t(`days.${DAY_KEYS[d.dow]}`)).join(', ');
    return `${days}: ${sorted[0]!.start}\u2013${sorted[0]!.end}`;
  }
  return sorted.map((d) => `${t(`days.${DAY_KEYS[d.dow]}`)} ${d.start}\u2013${d.end}`).join(', ');
}

/** Convert a camelCase key to a readable label: "autoRecord" → "Auto-Record" */
function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\bFps\b/i, 'FPS')
    .replace(/\bPii\b/i, 'PII')
    .replace(/\bDnc\b/i, 'DNC')
    .replace(/\bSla\b/i, 'SLA')
    .replace(/\bCid\b/i, 'CID');
}

/**
 * Summarise a group-field object for display.
 * Booleans: list enabled items. Numbers/strings: show "key: value" for non-default.
 */
function formatGroupValue(value: unknown, t: TFunction): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '—';
  const obj = value as Record<string, unknown>;
  const entries = Object.entries(obj);
  if (entries.length === 0) return '—';

  const boolEntries = entries.filter(([, v]) => typeof v === 'boolean');
  const otherEntries = entries.filter(([, v]) => typeof v !== 'boolean');

  const parts: string[] = [];

  // Show enabled booleans
  const enabled = boolEntries.filter(([, v]) => v);
  if (enabled.length > 0) {
    parts.push(enabled.map(([k]) => humanizeKey(k)).join(', '));
  } else if (boolEntries.length > 0 && otherEntries.length === 0) {
    return t('recipe.noneEnabled');
  }

  // Show non-default non-boolean values (skip zeros, empty strings, common defaults)
  for (const [k, v] of otherEntries) {
    if (v === '' || v === null || v === undefined) continue;
    parts.push(`${humanizeKey(k)}: ${v}`);
  }

  return parts.length > 0 ? parts.join(', ') : t('recipe.defaults');
}

function formatValue(step: StepInfo, rawAnswer: unknown, t: TFunction): React.ReactNode {
  // Schedule field
  if (step.field === 'businessHours') {
    return formatSchedule(rawAnswer ?? step.value, t);
  }
  // Use step.value first (server already resolves labels for select_one, boolean)
  const display = step.value ?? rawAnswer;
  if (display === null || display === undefined) return '—';
  if (typeof display === 'boolean') return display ? t('fields.yes') : t('fields.no');
  if (Array.isArray(display)) {
    return display.length === 0 ? '—' : t('recipe.selected', { count: display.length });
  }
  if (typeof display === 'object') {
    const obj = display as Record<string, unknown>;
    // Flow builder data — show summary
    if (Array.isArray(obj.nodes)) {
      const nodeCount = (obj.nodes as unknown[]).length;
      const edgeCount = Array.isArray(obj.edges) ? (obj.edges as unknown[]).length : 0;
      return t('recipe.flowSummary', { nodes: nodeCount, edges: edgeCount });
    }
    return formatGroupValue(display, t);
  }
  return String(display);
}

export function RecipePreview({ preview, onConfirm, onEdit }: RecipePreviewProps): React.ReactElement {
  const { t } = useTranslation();
  const dataSteps = preview.steps.filter(
    (s: StepInfo) => s.state === 'completed' || s.state === 'prefilled',
  );

  return (
    <div className="sp-recipe-preview">
      <div className="sp-recipe-header">
        <span className="sp-recipe-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
          </svg>
        </span>
        <h4 className="sp-recipe-title">{preview.workflowName}</h4>
      </div>
      <table className="sp-recipe-table">
        <tbody>
          {dataSteps.map((step: StepInfo) => (
            <tr key={step.id} className="sp-recipe-row">
              <td className="sp-recipe-label">{step.label}</td>
              <td className="sp-recipe-value">
                {formatValue(step, preview.answers[step.field], t)}
              </td>
              {onEdit && (
                <td className="sp-recipe-edit">
                  <button
                    className="sp-recipe-edit-btn"
                    onClick={() => onEdit(step.id)}
                    aria-label={t('recipe.editStep', { label: step.label })}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sp-recipe-actions">
        {onEdit && (
          <button className="sp-btn sp-btn-secondary" onClick={() => onEdit(dataSteps[0]?.id ?? '')}>
            {t('recipe.edit')}
          </button>
        )}
        {onConfirm && (
          <button className="sp-btn sp-btn-primary" onClick={onConfirm}>
            {t('recipe.execute')}
          </button>
        )}
      </div>
    </div>
  );
}
